import { Labels } from '../../global/labels';
import { UIElement } from '../ui-element';
import { resolveVideoHost } from '../../global/helpers';
import { createElement } from '../../global/render-api';
import { TabIndex } from '../../global/keyboard';
import { UIIcon } from '../icon/ui-icon';
import { createPictureWithSources } from '../../global/ui-helpers';
import { browser } from '../../global/browser';
import { Component } from '../../decorators/component.decorator';
import { Attribute } from '../../decorators/attribute.decorator';
import { Child } from '../../decorators/child.decorator';
import type { TranslationLabels } from '../../types/labels.type';
import type {
    UIVideoHosts,
    UIVideoSources,
    UIVideoType,
    UIVideoTypes,
} from './ui-video.type';
import styles from './ui-video.css';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UIVideo
 * @element ui-video
 * @classdesc Represents a class for <code>ui-video</code> element.
 * Marketing element allows to play videos from YouTube. Maybe in
 * future more video sources will be implemented.
 * @property {string} [poster] {@attr poster} Background image for video container.
 * @property {string} [posterMobile] {@attr poster-mobile} Background image for video container
 * for smaller screens.
 * @property {string} [src] {@attr src} URL of the video.
 * Only YouTube video URLs are supported at the moment.
 * @property {string} [alt=""] {@attr alt} alternate text for image.
 * Same as alt for &lt;img>.
 * @property {("external" | "embed" | "modal")} [type=external]
 *   {@attr type} How video should be opened.
 *   {@desc external: opens video in new tab/window}
 *   {@desc embed: insert video just instead of wallpaper}
 *   {@desc modal: video will be opened in modal dialog}
 * @property {HTMLButtonElement} button Shortcut to play button element.
 * @example
 * <ui-video
 *    poster="assets/mocks/images/offer08.jpg"
 *    type="modal"
 *    src="https://www.youtube.com/watch?v=qRc7PrxxJNs"></ui-video>
 */
@Component({ tag: 'ui-video', styles })
export class UIVideo extends UIElement {
    /**
     * @type {{UNKNOWN: string, YOUTUBE: string}}
     */
    static readonly HOSTS: UIVideoHosts = {
        UNKNOWN: 'unknown',
        YOUTUBE: 'youtube',
    } as const;

    /**
     * @type {{External: string, Embed: string, Modal: string}}
     */
    static readonly VideoTypes: UIVideoTypes = {
        External: 'external',
        Embed: 'embed',
        Modal: 'modal',
    };

    /**
     * @type {Labels}
     * @readonly
     */
    static get labels(): TranslationLabels.Labels {
        return Labels.attach('ui-video', {
            play: 'Play video',
        });
    }

    @Attribute(String) poster?: string;
    @Attribute(String) posterMobile?: string;
    @Attribute(String) src?: string;
    @Attribute(String) alt: string = '';
    @Attribute(String) type: UIVideoType = UIVideo.VideoTypes.External;

    @Child('.ui-video__icon-container') button?: HTMLButtonElement;

    /**
     * Extracts from URL YouTube video id.
     * @returns {string}
     * @private
     */
    private getYoutubeVideoId(): string {
        const vidRegExp =
            /(youtu\.be\/|youtube\.com\/(watch\?(.*&)?v=|(embed|v)\/))([^?&"'>]+)/;
        try {
            return String(this.src).match(vidRegExp)![5];
        } catch (e) {
            return '';
        }
    }

    /**
     * Creates video modal dialog.
     * @returns {UIModal}
     * @private
     */
    private createVideoWrapper(): HTMLElement | null {
        const videoHost = resolveVideoHost(this.src!);
        const videoId = this.getYoutubeVideoId();
        let videoURL = '';
        if (videoHost === UIVideo.HOSTS.YOUTUBE && videoId) {
            videoURL = `https://www.youtube-nocookie.com/embed/${videoId}?rel=0&autoplay=1`;
        } else {
            return null;
        }
        const wrapper = this.createElement({
            tagName: 'div',
            classList: { 'ui-video__wrapper': true },
        });
        const iframe = this.createElement({
            tagName: 'iframe',
            attributes: {
                tabindex: TabIndex.Active,
                allow: 'accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture',
                allowfullscreen: true,
                src: videoURL,
                frameborder: 0,
            },
        });
        wrapper.appendChild(iframe);
        return wrapper;
    }

    /**
     * Video open callback.
     * @param {Event} e
     * @private
     */
    private handleVideoOpen = (e: Event): void => {
        e.preventDefault();
        const wrapper = this.createVideoWrapper();
        this.classList.add('-playing');
        switch (this.type) {
            case 'modal':
                this.dispatchCustomEvent('modal-open', { content: wrapper });
                break;
            case 'embed':
                if (wrapper) {
                    this.appendChild(wrapper);
                }
                break;
            default:
                browser.run(() => window.open(this.src));
                break;
        }
    };

    /**
     * Start playing the video.
     */
    play(): void {
        this.button?.click();
    }

    /** @private */
    private subscribeEvents(): void {
        this.button?.addEventListener('click', this.handleVideoOpen);
    }

    /** @private */
    private unsubscribeEvents(): void {
        this.button?.removeEventListener('click', this.handleVideoOpen);
    }

    /**
     * @inheritDoc
     */
    override render(): void {
        if (this.poster) {
            const sources: Array<UIVideoSources> = [];
            if (this.posterMobile) {
                sources.push({
                    media: browser.getMediaQueryMap().Mobile,
                    srcset: this.posterMobile,
                });
            }
            const picture = createPictureWithSources(
                sources,
                this.poster,
                this.alt
            );
            this.append(createElement(picture)!);
        }
        this.appendChild(
            this.createElement({
                tagName: 'button',
                classList: {
                    'ui-video__icon-container': true,
                    '-iconed': true,
                },
                attributes: {
                    'type': 'button',
                    'aria-label': UIVideo.labels.play,
                },
                children: [
                    {
                        tagName: 'ui-icon',
                        attributes: {
                            glyph: 'play',
                            bgcolor: UIIcon.colors.ORANGE,
                            color: UIIcon.colors.WHITE,
                            size: 'large',
                        },
                        classList: { '-navicon': true },
                    },
                ],
            })
        );
    }

    /**
     * @inheritDoc
     */
    override hydrate(): void {
        this.subscribeEvents();
    }

    /**
     * @inheritDoc
     */
    override disconnect(): void {
        this.unsubscribeEvents();
    }

    /**
     * @inheritDoc
     */
    override reconnect(): void {
        this.subscribeEvents();
    }
}
